import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Phone, MessageSquare, FileText, Settings, ChevronDown, ChevronUp, Trash2, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { LogPhoneCallDialog } from "./LogPhoneCallDialog";
import { AddNoteDialog } from "./AddNoteDialog";
import { 
  COMMUNICATION_TYPE_LABELS, 
  COMMUNICATION_DIRECTION_LABELS,
  COMMUNICATION_STATUS_COLORS 
} from "@/config/constants";
import { formatDateTime } from "@/lib/formatters";

interface CommunicationLog {
  id: string;
  order_id: string | null;
  customer_id: string | null;
  communication_type: string;
  direction: string;
  subject: string | null;
  content: string | null;
  summary: string | null;
  from_address: string | null;
  to_address: string | null;
  status: string;
  error_message: string | null;
  metadata: Record<string, unknown> | null;
  related_entity_type: string | null;
  related_entity_id: string | null;
  created_by: string | null;
  created_at: string;
  profiles?: { first_name: string | null; last_name: string | null } | null;
}

interface CommunicationHistoryProps {
  orderId?: string;
  customerId?: string;
  maxHeight?: string;
}

const typeIcons: Record<string, React.ReactNode> = {
  email: <Mail className="h-4 w-4" />,
  phone: <Phone className="h-4 w-4" />,
  sms: <MessageSquare className="h-4 w-4" />,
  note: <FileText className="h-4 w-4" />,
  system: <Settings className="h-4 w-4" />,
};

export function CommunicationHistory({ orderId, customerId, maxHeight = "400px" }: CommunicationHistoryProps) {
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);

  const { data: logs, isLoading } = useQuery({
    queryKey: ["communication-logs", orderId, customerId],
    queryFn: async () => {
      let query = supabase
        .from("communication_logs")
        .select(`
          *,
          profiles:created_by (first_name, last_name)
        `)
        .order("created_at", { ascending: false });

      if (orderId) {
        query = query.eq("order_id", orderId);
      } else if (customerId) {
        query = query.eq("customer_id", customerId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as CommunicationLog[];
    },
    enabled: !!(orderId || customerId),
    staleTime: 0,
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });

  // Realtime subscription for automatic updates
  useEffect(() => {
    if (!orderId && !customerId) return;

    const filter = orderId 
      ? `order_id=eq.${orderId}` 
      : `customer_id=eq.${customerId}`;

    const channel = supabase
      .channel(`communication-logs-${orderId || customerId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "communication_logs",
          filter,
        },
        () => {
          queryClient.invalidateQueries({ 
            queryKey: ["communication-logs", orderId, customerId] 
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orderId, customerId, queryClient]);

  const deleteMutation = useMutation({
    mutationFn: async (logId: string) => {
      const { error } = await supabase
        .from("communication_logs")
        .delete()
        .eq("id", logId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["communication-logs"] });
      toast.success("Merkintä poistettu");
    },
    onError: () => {
      toast.error("Virhe", { description: "Merkinnän poistaminen epäonnistui" });
    },
  });

  const filteredLogs = logs?.filter((log) => 
    typeFilter === "all" || log.communication_type === typeFilter
  );

  const toggleExpanded = (id: string) => {
    const newSet = new Set(expandedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setExpandedIds(newSet);
  };

  const handleLogSaved = () => {
    queryClient.invalidateQueries({ queryKey: ["communication-logs"] });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => setPhoneDialogOpen(true)}>
          <Phone className="h-4 w-4 mr-2" />
          Kirjaa puhelu
        </Button>
        <Button variant="outline" size="sm" onClick={() => setNoteDialogOpen(true)}>
          <FileText className="h-4 w-4 mr-2" />
          Lisää muistiinpano
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Suodata:</span>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Kaikki</SelectItem>
            <SelectItem value="email">Sähköpostit</SelectItem>
            <SelectItem value="phone">Puhelut</SelectItem>
            <SelectItem value="sms">SMS</SelectItem>
            <SelectItem value="note">Muistiinpanot</SelectItem>
            <SelectItem value="system">Järjestelmä</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground ml-auto">
          {filteredLogs?.length || 0} merkintää
        </span>
      </div>

      {/* Timeline */}
      <ScrollArea style={{ height: maxHeight }}>
        {filteredLogs?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Ei viestintähistoriaa
          </div>
        ) : (
          <div className="space-y-3 pr-4">
            {filteredLogs?.map((log) => (
              <Collapsible key={log.id} open={expandedIds.has(log.id)}>
                <div className={`border rounded-lg p-3 ${
                  log.status === "suppressed"
                    ? "border-amber-300 bg-amber-50 opacity-75"
                    : log.status === "failed"
                    ? "border-red-300 bg-red-50"
                    : ""
                }`}>
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`p-2 rounded-full ${
                      log.status === "suppressed"
                        ? "bg-amber-100 text-amber-600"
                        : log.direction === "inbound"
                        ? "bg-blue-100 text-blue-600"
                        : log.direction === "internal"
                        ? "bg-gray-100 text-gray-600"
                        : "bg-green-100 text-green-600"
                    }`}>
                      {log.status === "suppressed"
                        ? <BellOff className="h-4 w-4" />
                        : typeIcons[log.communication_type] || <FileText className="h-4 w-4" />
                      }
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">
                          {COMMUNICATION_TYPE_LABELS[log.communication_type as keyof typeof COMMUNICATION_TYPE_LABELS] || log.communication_type}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {COMMUNICATION_DIRECTION_LABELS[log.direction as keyof typeof COMMUNICATION_DIRECTION_LABELS] || log.direction}
                        </Badge>
                        {log.status !== "completed" && (
                          <Badge className={`text-xs ${COMMUNICATION_STATUS_COLORS[log.status as keyof typeof COMMUNICATION_STATUS_COLORS] || ""}`}>
                            {log.status}
                          </Badge>
                        )}
                      </div>

                      {log.subject && (
                        <p className="text-sm font-medium mt-1">{log.subject}</p>
                      )}

                      {log.summary && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {log.summary}
                        </p>
                      )}

                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <span>
                          {formatDateTime(log.created_at)}
                        </span>
                        {log.profiles && (
                          <span>
                            • {log.profiles.first_name} {log.profiles.last_name}
                          </span>
                        )}
                        {log.to_address && (
                          <span className="truncate">• {log.to_address}</span>
                        )}
                      </div>

                      {log.error_message && (
                        <p className="text-sm text-red-600 mt-1">
                          Virhe: {log.error_message}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      {log.content && (
                        <CollapsibleTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => toggleExpanded(log.id)}
                          >
                            {expandedIds.has(log.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                      )}
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteMutation.mutate(log.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <CollapsibleContent>
                    {log.content && (
                      <div className="mt-3 pt-3 border-t">
                        <pre className="text-sm whitespace-pre-wrap font-sans bg-muted p-3 rounded">
                          {log.content}
                        </pre>
                      </div>
                    )}
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Dialogs */}
      <LogPhoneCallDialog
        open={phoneDialogOpen}
        onOpenChange={setPhoneDialogOpen}
        orderId={orderId}
        customerId={customerId}
        onSaved={handleLogSaved}
      />

      <AddNoteDialog
        open={noteDialogOpen}
        onOpenChange={setNoteDialogOpen}
        orderId={orderId}
        customerId={customerId}
        onSaved={handleLogSaved}
      />
    </div>
  );
}
